import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { IBooking } from "./Model";
import { Alert, Button, Col, Form, Modal } from "react-bootstrap";
import dayjs from "dayjs";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { IKontonummer, kontonummer } from "../../../libs/kontonummer";
import { trackPageView } from "../../Shared/Tracking";
import { currentLanguage } from "../Settings";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { CountrySelector } from "../../Shared/Components/CountrySelector";

const strings = getStrings().component.compensationForm;

type FormParameters = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	trip: string;
	paymentMethod: "Utbetalning avi" | "Utbetalning till bankkonto" | "Värdebevis";
	accountType: string;
	account: string;
	swift: string;
	iban: string;
	personid: string;
	address: string;
	zip: string;
	city: string;
	country: string;
}

export const CompensationDialog = (props: { booking: IBooking, show: boolean, onClosed: () => void }) => {

	const [showThankYou, setShowThankYou] = useState(false);
	const [showErrorMessage, setShowErrorMessage] = useState(false);
	const [bankkonto, setBankkonto] = useState<IKontonummer>();

	const [isSubmitting, setIsSubmitting] = useState(false);

	const { register, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm<FormParameters>({ mode: 'onTouched', defaultValues: { paymentMethod: "Värdebevis", accountType: "sv-konto" } });

	const accountType = watch("accountType");
	const paymentMethod = watch("paymentMethod");
	const country = watch("country");

	const showAccountFields = paymentMethod === "Utbetalning till bankkonto";
	const showAddressFields = paymentMethod === "Utbetalning avi" || (paymentMethod === "Utbetalning till bankkonto" && accountType === "intl-konto");

	const snalltagSegments = (props.booking?.outbound?.segments || []).concat(props.booking?.inbound?.segments || []).filter(x => x.serviceType.code.startsWith("ST")); //Finns det bättre sätt?
	const firstSegment = snalltagSegments && snalltagSegments.length > 0 ? snalltagSegments[0] : undefined;
	const lastSegment = snalltagSegments && snalltagSegments.length > 1 ? snalltagSegments[snalltagSegments.length -1] : undefined;

	useEffect(() => {
		if (props.show)
			trackPageView("begar-ersattning", "Begär ersättning");
	}, [props.show]);


	const onClose = () => {
		setShowThankYou(false);
		setShowErrorMessage(false);
		setIsSubmitting(false);
		setBankkonto(undefined);
		props.onClosed();
	}

	const onSubmit = async (data: FormParameters) => {
		setIsSubmitting(true);

		let country = data.country;
		if (paymentMethod == "Utbetalning till bankkonto" && accountType == "sv-konto")
			country = "Sverige (SE)";

		const postData = {
			'orgid': '00D5J000000DIld',
			'retURL': 'https://snalltaget.se/kontakt/formular-mottaget',
			'email': data.email,
			'phone': data.phone,
			'priority': 'Low',
			'00N5J000002Y8VM': 'Ersättning vid försening',
			'00N5J000002aTUz': currentLanguage !== "sv-SE" ? "English" : "Svenska",
			'00N5J000002Y8Va': props.booking?.bookingNumber ?? "",
			'subject': 'Ersättning vid försening',
			'description': data.trip,
			'00N5J000002Y8Vc': data.firstName ?? "",
			'00N5J000002Y8Vd': data.lastName ?? "",
			'00N5J0000039O5D': data.paymentMethod ?? "",
			'00N5J000002Y8VH': data.address ?? "",
			'00N5J000002Y8VI': data.zip ?? "",
			'00N5J000002Y8VF': data.city ?? "",
			"00N5J000002Y8VG": country,
			'00N5J000002Y8Vb': accountType === "sv-konto" ? bankkonto?.clearing_number ?? "" : data.swift ?? "",
			'00N5J000002Y8VR': accountType === "sv-konto" ? bankkonto?.account_number ?? "" : data.iban ?? "",
			'00N5J0000039O5I': data.personid ?? "",
			'00N5J000002Y8VP': firstSegment ? dayjs(firstSegment.departure).format("YYYY-MM-DD") : "", // Datum utresa
			'00N5J000002aQaf': lastSegment ? dayjs(lastSegment.departure).format("YYYY-MM-DD") : ""  // Datum hemresa
		};

		// console.debug("submit", postData);
		// return;

		const response = await fetch('/salesforce/webtocase', {
			method: 'POST',
			body: JSON.stringify(postData),
			credentials: "same-origin",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				"Accept-Language": 'sv-SE'
			}
		});

		if (response.ok) {
			console.log("Salesforce OK: " + response.status);
			setShowErrorMessage(false);
			setShowThankYou(true);
			if (props.booking?.bookingNumber)
				localStorage.setItem(`ersattning-${props.booking?.bookingNumber}`, "true");
		}
		else {
			console.log("Saleforce error: " + response.status)
			setShowErrorMessage(true);
		}
		setIsSubmitting(false);
	}

	const isValidKontoNr = (kontonr:string) => {
		let result = kontonummer(kontonr);
		console.log(result);
		if (result) {
			let konto = result as IKontonummer
			setBankkonto(konto);
			return true;
		}
		else
		{
			setBankkonto(undefined);
			return false;
		}
	}

	const countrySelected = (countryName: string, countryCode: string) => {
		// console.debug("countrySelected", countryName, countryCode);

		//bara för att undvika att det triggas innan man gjort något
		if (!country && !countryName)
			return;

		setValue("country", countryName ? `${countryName} (${countryCode})` : null);
		trigger("country");
	}


	return <Modal show={props.show} centered backdrop="static" className="compensation-form-dialog" size="xl">
		<Form onSubmit={handleSubmit(data => onSubmit(data))} id="compensationform">
			<Modal.Header><Modal.Title>{strings.modalTitle}</Modal.Title></Modal.Header>
			<Modal.Body>
				{ showThankYou && <p>{strings.thankYouText}</p> }
				{ props.booking && !showThankYou &&
					<div>
						<p>{strings.infoText}</p>
						<Form.Group>
							<Form.Label>{strings.reservationNo}: <strong>{props.booking.bookingNumber}</strong></Form.Label>
						</Form.Group>
						<Form.Row>
							<Form.Group as={Col} xs={12} sm={6} controlId="form-element-00N5J000002Y8Vc">
								<Form.Label>{strings.firstName}</Form.Label>
								<Form.Control type="text" placeholder="" readOnly={props.booking.customer?.firstName !== undefined} maxLength={50} defaultValue={props.booking.customer?.firstName} {...register("firstName", { required: true })} />
								{errors.firstName && <div className="text-danger">{strings.firstName} {strings.required}</div>}
							</Form.Group>
							<Form.Group as={Col} xs={12} sm={6} controlId="form-element-00N5J000002Y8Vd">
								<Form.Label>{strings.lastName}</Form.Label>
								<Form.Control type="text" placeholder="" readOnly={props.booking.customer?.lastName !== undefined} maxLength={50} defaultValue={props.booking.customer?.lastName} {...register("lastName", { required: true })}  />
								{errors.lastName && <div className="text-danger">{strings.lastName} {strings.required}</div>}
							</Form.Group>
						</Form.Row>
						<Form.Row>
							<Form.Group as={Col} xs={12} sm={6} controlId="form-element-email">
								<Form.Label>{strings.email}</Form.Label>
								<Form.Control type="email" placeholder="" readOnly={props.booking.customer?.email !== undefined} maxLength={80} defaultValue={props.booking.customer?.email} {...register("email", { required: true })}  />
								{errors.email && <div className="text-danger">{strings.email} {strings.required}</div>}
							</Form.Group>
							<Form.Group as={Col} xs={12} sm={6} controlId="form-element-phone" >
								<Form.Label>{strings.phone}</Form.Label>
								<Form.Control type="tel" placeholder="" maxLength={40} defaultValue={props.booking.customer?.phoneNumber} {...register("phone")}   />
							</Form.Group>
						</Form.Row>
						<Form.Group controlId="form-element-trip">
							<Form.Label>{strings.selectTrip}</Form.Label>
							<Form.Control as="select" {...register("trip")} custom>
								{ snalltagSegments?.map((segment, idx) =>
									<option key={idx}>{segment.from.name} - {segment.to.name}, {segment.serviceName} ({dayjs(segment.departure).format("YYYY-MM-DD HH:mm")})</option>
								)}
								{ snalltagSegments && snalltagSegments.length > 1 &&
									<option value={snalltagSegments.map(segment => `${segment.serviceName} (${dayjs(segment.departure).format("YYYY-MM-DD")}) `)}>{strings.allTrips}</option>
								}
							</Form.Control>
						</Form.Group>
						<Form.Group controlId="form-element-paymentmethod">
							<Form.Label>{strings.selectPaymentmethod}</Form.Label>
							<Form.Control as="select"  {...register("paymentMethod")} custom>
								<option value="Värdebevis">{strings.optionVoucher}</option>
								{currentLanguage === "sv-SE" && <option value="Utbetalning avi">Kontant utbetalning till avi</option> }
								<option value="Utbetalning till bankkonto">{strings.optionAccount}</option>
							</Form.Control>
						</Form.Group>
						{ showAccountFields && <>
							<Form.Group>
								<Form.Check inline type="radio" name="kontotyp" defaultChecked={true} { ...register("accountType") } value="sv-konto" label={strings.accountSwedish} />
								<Form.Check inline type="radio" name="kontotyp" defaultChecked={false} { ...register("accountType") } value="intl-konto" label={strings.accountIntl} />
							</Form.Group>
							{ accountType === "sv-konto" &&
								<Form.Row>
									<Form.Group as={Col} xs={12} sm={6} controlId="form-element-00N5J000002Y8Vb">
										<Form.Label>{strings.account}</Form.Label>
										<Form.Control type="text" {...register("account", { required: true, maxLength: 20, validate: v => isValidKontoNr(v)})} />
										{errors.account && <div className="text-danger">{strings.account} {strings.notvalid}</div>}
									</Form.Group>
									<Col xs={12} sm={6}>
										<br />
										{bankkonto && <span>Bank: <strong>{bankkonto.bank_name}</strong><br />{strings.clearingNo}: <strong>{bankkonto.clearing_number}</strong>, {strings.accountNo}: <strong>{bankkonto.account_number}</strong></span>}
									</Col>
								</Form.Row>
							}
							{ accountType === "intl-konto" &&
								<Form.Row>
									<Form.Group as={Col} xs={12} sm={6} controlId="form-element-00N5J000002Y8Vb">
										<Form.Label>SWIFT/BIC</Form.Label>
										<Form.Control type="text" {...register("swift", { required: true, maxLength: 11 })} />
										{errors.swift && <div className="text-danger">SWIFT/BIC {strings.required}</div>}
									</Form.Group>
									<Form.Group as={Col} xs={12} sm={6} controlId="form-element-00N5J000002Y8VR">
										<Form.Label>IBAN</Form.Label>
										<Form.Control type="text" {...register("iban", { required: true, maxLength: 30 })} />
										{errors.iban && <div className="text-danger">IBAN {strings.required}</div>}
									</Form.Group>
								</Form.Row>
							}

						</>}
						{ showAddressFields && <>
							{ accountType !== "intl-konto" && <Form.Group controlId="form-element-personnr">
								<Form.Label>Personnummer</Form.Label>
								<Form.Control type="text" {...register("personid", { required: true, maxLength: 12, pattern:/^\d{12}$/ })} />
								{errors.personid && <div className="text-danger">{strings.personid} {strings.required} ÅÅÅÅMMDDNNNN</div>}
							</Form.Group> }
							<Form.Group controlId="form-element-00N5J000002Y8VH">
								<Form.Label>{strings.address}</Form.Label>
								<Form.Control type="text" {...register("address", { required: true, maxLength: 30 })} />
								{errors.address && <div className="text-danger">{strings.address} {strings.required}</div>}
							</Form.Group>
							<Form.Row>
								<Form.Group as={Col} xs={4} controlId="form-element-00N5J000002Y8VI">
									<Form.Label>{strings.zip}</Form.Label>
									<Form.Control type="text" {...register("zip", { required: true, maxLength: 6 })} />
									{errors.zip && <div className="text-danger">{strings.zip} {strings.required}</div>}
								</Form.Group>
								<Form.Group as={Col} xs={8} controlId="form-element-00N5J000002Y8VF">
									<Form.Label>{strings.city}</Form.Label>
									<Form.Control type="text" {...register("city", { required: true, maxLength: 30 })} />
									{errors.city && <div className="text-danger">{strings.city} {strings.required}</div>}
								</Form.Group>
							</Form.Row>
							<Form.Group>
								<Form.Label>{strings.country}</Form.Label>
								<CountrySelector onCountrySelected={countrySelected} required />
								<Form.Control type="hidden" {...register("country", { required: true })}/>
								{ errors.country && <div className="text-danger">{strings.country} {strings.required}</div> }
							</Form.Group>
						</>}
						<Alert variant="danger" show={showErrorMessage}>{strings.submitError}</Alert>
					</div>
				}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" disabled={isSubmitting} onClick={onClose}>{strings.closeText}</Button>
				{ !showThankYou && <Button type="submit" variant="primary" disabled={isSubmitting}>{strings.submitText} {isSubmitting &&  <SpinnerSmall />}</Button> }
			</Modal.Footer>
		</Form>
	</Modal>;
}